import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Button as MuiButton,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
  GridRowSpacingParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { templatesSx } from '@src/components/_shared/sxStyles/Config/configTableList';
import QueryKeys from '@src/constants/queryKeys';
import useTemplateMutations from '@src/hooks/api/mutations/useTemplateMutations';
import useGetControlModeTemplates from '@src/hooks/api/queries/modes/useGetControlModeTemplates';
import useAlias from '@src/hooks/useAlias';
import { ControlModeTemplateSettings } from '@src/types/ControlModes';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import ControlModesTemplateCreateEditModal from './ControlModesTemplateCreateEditModal';
import DeleteTemplateConfirmationDialog from './components/DeleteTemplateConfirmationDialog';
import ImportResultDialog from './components/ImportResultDialog';
import TemplatesToolbar from './components/TemplatesToolbar';
import {
  modeTemplatesMain,
  modeTemplatesWrapper,
  StyledDialogHeader,
} from './styles';
import {
  ImportControlModeTemplateResponseDto,
  TemplateExtended,
  TemplateRow,
  TemplatesListProps,
} from './types';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export default function TemplatesList({
  selectionMode = false,
  singleSelection = false,
  onTemplatesSelected,
  onEditTemplate,
}: TemplatesListProps) {
  const alias = useAlias();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateExtended | null>(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [handleSaveRef, setHandleSaveRef] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | undefined>(
    undefined,
  );
  const [importResult, setImportResult] =
    useState<ImportControlModeTemplateResponseDto | null>(null);
  const [importResultDialogOpen, setImportResultDialogOpen] = useState(false);
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const {
    data: templatesList,
    isFetching,
    isRefetching,
  } = useGetControlModeTemplates();
  const { deleteControlModeTemplate } = useTemplateMutations(templateToDelete);

  // Call onTemplatesSelected when rowSelectionModel changes in selection mode
  useEffect(() => {
    if (selectionMode && onTemplatesSelected && templatesList) {
      const selectedTemplates = templatesList.filter((template) =>
        rowSelectionModel.includes(template.id as string),
      );
      onTemplatesSelected(selectedTemplates);
    }
  }, [selectionMode, onTemplatesSelected, rowSelectionModel, templatesList]);

  const handleEditClick = (id: GridRowId) => {
    if (apiRef.current !== undefined) {
      setSelectedTemplate(apiRef.current.getRow(id));
    }
    setIsCreatingTemplate(false);
    setConfigModalOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedTemplate(null);
    setIsCreatingTemplate(true);
    setConfigModalOpen(true);
  };

  const handleConfigModalClose = () => {
    setConfigModalOpen(false);
    setHandleSaveRef(null);
    setIsCreatingTemplate(false);
  };

  const handleSaveClick = () => {
    if (handleSaveRef) {
      handleSaveRef();
    }
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setTemplateToDelete(id.toString());
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      await deleteControlModeTemplate.mutateAsync({});
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.controlModeTemplates],
      });
      setConfirmDialogOpen(false);
      setTemplateToDelete(undefined);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setTemplateToDelete(undefined);
  };

  const handleImportSuccess = (
    result: ImportControlModeTemplateResponseDto,
  ) => {
    setImportResult(result);
    setImportResultDialogOpen(true);
  };

  const handleImportResultDialogClose = () => {
    setImportResultDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: alias('Name'),
      width: 200,
      editable: true,
    },
    {
      field: 'settings',
      headerName: alias('Controller Fields'),
      flex: 1,
      editable: true,
      renderCell: (
        params: GridCellParams<
          TemplateRow,
          string,
          ControlModeTemplateSettings[]
        >,
      ) => {
        const settings: ControlModeTemplateSettings[] = params.row.settings;
        return settings.map((setting) => setting.controllerField).join(', ');
      },
    },
    {
      field: 'updatedAt',
      headerName: alias('Updated At'),
      width: 220,
      editable: false,
      renderCell: (
        params: GridCellParams<TemplateRow, Date | undefined, Date | undefined>,
      ) => {
        const updatedAt = params.row.updatedAt;
        if (!updatedAt) {
          return '-';
        }

        return dayjs.utc(updatedAt).local().format('MMM DD, YYYY, hh:mm A');
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: alias('Actions'),
      description: alias('Actions'),
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }: GridRowParams<TemplateRow>) => {
        // In selection mode with onEditTemplate prop
        if (selectionMode && onEditTemplate) {
          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label={alias('Edit')}
              className='textPrimary'
              onClick={() => {
                // Find the full template in templatesList
                const template = templatesList?.find((t) => t.id === id);
                if (template) {
                  onEditTemplate(template);
                }
              }}
              color='primary'
              sx={{ opacity: 0.8 }}
              key={`edit-icon-cell-${id}`}
            />,
          ];
        }

        // Normal mode (not selection mode)
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={alias('Edit')}
            className='textPrimary'
            onClick={() => {
              handleEditClick(id);
            }}
            color='primary'
            sx={{ opacity: 0.8 }}
            key={`edit-icon-cell-${id}`}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={alias('Delete')}
            onClick={handleDeleteClick(id)}
            color='error'
            sx={{ opacity: 0.8 }}
            key={`delete-icon-cell-${id}`}
          />,
        ];
      },
    },
  ];

  const rows: TemplateRow[] =
    templatesList?.map((template) => ({
      id: template.id as string,
      name: template.name,
      settings: template.settings ?? [],
      updatedAt: template.updatedAt,
    })) || [];

  return (
    <Box component='main' sx={selectionMode ? {} : modeTemplatesMain}>
      <Box component='section' sx={selectionMode ? {} : modeTemplatesWrapper}>
        <DataGridPro
          getRowClassName={() => 'MuiDataGrid-clickable templates-row'}
          rows={rows}
          hideFooter
          columns={columns}
          apiRef={apiRef}
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            if (singleSelection && newRowSelectionModel.length > 0) {
              const lastSelectedId =
                newRowSelectionModel[newRowSelectionModel.length - 1];
              setRowSelectionModel([lastSelectedId]);
            } else {
              setRowSelectionModel(newRowSelectionModel);
            }
          }}
          checkboxSelection
          loading={isRefetching || isFetching}
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            density: 'comfortable',
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          slots={selectionMode ? {} : { toolbar: TemplatesToolbar }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            ...(selectionMode
              ? {}
              : {
                  toolbar: {
                    handleAdd: handleCreateClick,
                    onImportSuccess: handleImportSuccess,
                  },
                }),
          }}
          getRowSpacing={(_params: GridRowSpacingParams) => ({
            top: 9,
            bottom: 4,
          })}
          sx={templatesSx.wrapper}
          localeText={{
            toolbarColumns: alias('Columns'),
            toolbarFilters: alias('Filters'),
            toolbarExport: alias('Export'),
            toolbarQuickFilterPlaceholder: alias('Search...'),
          }}
        />

        {!selectionMode && (
          <Dialog
            open={configModalOpen}
            onClose={handleConfigModalClose}
            fullWidth
            maxWidth='md'
          >
            <StyledDialogHeader>
              <Typography
                variant='h6'
                sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
              >
                {isCreatingTemplate
                  ? alias('Create Template')
                  : alias('Edit Template: {name}', {
                      name: selectedTemplate?.name || '',
                    })}
              </Typography>
              <IconButton
                onClick={handleConfigModalClose}
                sx={{ mr: -2, mt: -1 }}
              >
                <CloseIcon />
              </IconButton>
            </StyledDialogHeader>
            <DialogContent dividers>
              {isCreatingTemplate ? (
                <ControlModesTemplateCreateEditModal
                  onClose={handleConfigModalClose}
                  onSave={(
                    saveFunction: () => Promise<void>,
                    saving: boolean,
                  ) => {
                    setHandleSaveRef(() => saveFunction);
                    setIsSaving(saving);
                  }}
                />
              ) : (
                selectedTemplate && (
                  <ControlModesTemplateCreateEditModal
                    templateId={String(selectedTemplate.id)}
                    templateName={selectedTemplate.name}
                    onClose={handleConfigModalClose}
                    onSave={(
                      saveFunction: () => Promise<void>,
                      saving: boolean,
                    ) => {
                      setHandleSaveRef(() => saveFunction);
                      setIsSaving(saving);
                    }}
                  />
                )
              )}
            </DialogContent>
            <DialogActions>
              <MuiButton
                variant='contained'
                color='primary'
                onClick={handleSaveClick}
                disabled={isSaving}
              >
                {alias('SAVE')}
              </MuiButton>
            </DialogActions>
          </Dialog>
        )}

        <DeleteTemplateConfirmationDialog
          open={confirmDialogOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />

        <ImportResultDialog
          open={importResultDialogOpen}
          onClose={handleImportResultDialogClose}
          result={importResult}
        />
      </Box>
    </Box>
  );
}
